import React, {
  useState,
  useEffect,
  useCallback,
  useMemo,
  useRef,
} from "react";
import {
  Card,
  Button,
  Select,
  Table,
  Typography,
  Row,
  Col,
  Spin,
  notification,
  Divider,
  Switch,
  Space,
  message,
} from "antd";
import {
  ArrowLeftOutlined,
  FileTextOutlined,
  DollarOutlined,
} from "@ant-design/icons";
import { useNavigate } from "react-router-dom";
import {
  getOverpaymentDetails,
  getUnpaidInvoicesForOverpayment,
  getFirstPageUnpaidInvoicesForOverpayment,
  getAllPartners,
  getOtherOverpaymentsForAllocation,
} from "../../../api/accounting";
import { handleAllocateOverpayment as allocateOverpaymentHandler } from "../Columns/OverpaymentAllocation/allocationHandlers";
import getOverpaymentColumns from "../Columns/OverpaymentAllocation/overpaymentColumns";
import getInvoiceColumns from "../Columns/OverpaymentAllocation/invoiceColumns";
import getOverpaymentDetailColumns from "../Columns/OverpaymentAllocation/overPaymentDetailColumns";
import "bootstrap/dist/css/bootstrap.min.css";
import "../Utils/BankStatements.css"; // reusing the same CSS for consistent styling

const { Text, Title } = Typography;
const { Option } = Select;

// Helper to parse numbers that may contain commas, dollar signs, or other formatting
// parseFloat("18,132") returns 18, but we want 18132
// parseFloat("$20,670.60") returns NaN, but we want 20670.60
const parseNumberWithCommas = (value) => {
  if (value == null || value === undefined || value === "") return 0;
  // Convert to string and remove currency symbols ($, €, £, etc.), commas, and spaces
  const cleanedValue = String(value)
    .replace(/[$€£¥₹,\s]/g, "") // Remove currency symbols, commas, and spaces
    .trim();
  const parsed = parseFloat(cleanedValue);
  return isNaN(parsed) ? 0 : parsed;
};

const OverPaymentAllocation = ({
  overpaymentId,
  selectedBrand,
  selectedBrands: selectedBrandsProp,
  onSuccess,
  onCancel,
  onViewPartner,
}) => {
  // Support both single brand and multiple brands
  const selectedBrands =
    selectedBrandsProp || (selectedBrand ? [selectedBrand] : []);
  const primaryBrand = selectedBrands.length > 0 ? selectedBrands[0] : null;
  const navigate = useNavigate();
  const [loadingOverpayment, setLoadingOverpayment] = useState(false);
  const [overpayment, setOverpayment] = useState(null);
  const [allocations, setAllocations] = useState([]);
  const [allocationStatus, setAllocationStatus] = useState(null);
  const [partners, setPartners] = useState([]);
  const [selectedPartner, setSelectedPartner] = useState(null);
  const [invoices, setInvoices] = useState([]);
  const [otherOverpayments, setOtherOverpayments] = useState([]);
  const [selectedInvoiceIds, setSelectedInvoiceIds] = useState([]);
  const [selectedOverpaymentIds, setSelectedOverpaymentIds] = useState([]);
  const [loadingInvoices, setLoadingInvoices] = useState(false);
  const [loadingOtherOverpayments, setLoadingOtherOverpayments] =
    useState(false);
  const [loadingAllocation, setLoadingAllocation] = useState(false);
  const [allocationType, setAllocationType] = useState("invoice"); // 'invoice' or 'overpayment'
  const [showPaidInvoices, setShowPaidInvoices] = useState(false);
  const [pagination, setPagination] = useState({
    current: 1,
    pageSize: 50,
  });
  // Background loading state for hybrid loading
  const [isLoadingMore, setIsLoadingMore] = useState(false);
  const backgroundLoadingRef = useRef(false);
  const backgroundAbortRef = useRef(false);

  // Refs to prevent duplicate requests
  const fetchingOverpaymentRef = useRef(false);
  const fetchingInvoicesRef = useRef(false);
  const lastFetchKeyRef = useRef(null);

  // Fetch overpayment details
  const fetchOverpayment = useCallback(async () => {
    // Prevent duplicate requests
    if (fetchingOverpaymentRef.current) return;
    fetchingOverpaymentRef.current = true;

    setLoadingOverpayment(true);
    try {
      const data = await getOverpaymentDetails(overpaymentId);
      setOverpayment(data);

      // Set allocations from overpayment data
      if (data.allocations) {
        setAllocations(data.allocations);
      }

      // Update allocation status
      updateAllocationStatus(data);

      // If overpayment has a partner, pre-select it and fetch invoices
      // Check for partner_id first (more reliable), then fall back to partner_name
      // Handle case where partner might be an object with id property
      let partnerId = data.partner_id || data.partner;
      if (partnerId && typeof partnerId === "object") {
        partnerId = partnerId.id || partnerId.partner_id;
      }
      if (partnerId || data.partner_name) {
        // Fetch partners and use the returned data directly (avoid stale closure)
        // Support multiple brands for cross-brand allocation
        const brandIds = selectedBrands.map((b) => b.id).join(",");
        const partnersResponse = await getAllPartners({
          brand: brandIds,
        });
        // Deduplicate partners by ID (same partner can exist across multiple brands)
        const rawPartnersList = Array.isArray(partnersResponse.data)
          ? partnersResponse.data
          : [];
        const seenIds = new Set();
        const seenNames = new Set();
        const fetchedPartners = rawPartnersList.filter((partner) => {
          if (seenIds.has(partner.id)) {
            return false;
          }
          const normalizedName = (partner.name || "").toLowerCase().trim();
          if (seenNames.has(normalizedName)) {
            return false;
          }
          seenIds.add(partner.id);
          seenNames.add(normalizedName);
          return true;
        });
        setPartners(fetchedPartners);

        // Use partner_id directly if available, otherwise find by name
        if (partnerId) {
          setSelectedPartner(partnerId);
          fetchInvoices(partnerId, showPaidInvoices);
        } else if (data.partner_name) {
          // Find partner from the freshly fetched data by name
          const partner = fetchedPartners.find(
            (p) => p.name === data.partner_name
          );
          if (partner) {
            setSelectedPartner(partner.id);
            fetchInvoices(partner.id, showPaidInvoices);
          }
        }
      } else {
        // If no partner is associated with the overpayment, still fetch invoices
        // The backend will determine the partner from the overpayment
        fetchInvoices(null);
      }
    } catch (error) {
      console.error("Error fetching overpayment:", error);

      // Check if it's a 404 error (overpayment not found)
      if (error.response && error.response.status === 404) {
        notification.error({
          message: "Overpayment Not Found",
          description: `Overpayment with ID ${overpaymentId} does not exist.`,
        });
        // Navigate back to overpayments after a delay
        setTimeout(() => {
          handleBack();
        }, 2000);
      } else {
        notification.error({
          message: "Error",
          description: "Failed to load overpayment details",
        });
      }
    } finally {
      setLoadingOverpayment(false);
      fetchingOverpaymentRef.current = false;
    }
  }, [overpaymentId]);

  // Fetch overpayment data
  useEffect(() => {
    if (!overpaymentId) return;
    fetchOverpayment();
  }, [overpaymentId, fetchOverpayment]);

  // NOTE: Partners are fetched inside fetchOverpayment when the overpayment data loads
  // This ensures partners are only fetched once and not duplicated

  // Fetch other overpayments function
  const fetchOtherOverpayments = useCallback(
    async (partnerId) => {
      // Allow function to run without partnerId as long as we have overpaymentId and brand
      if (!selectedBrands || selectedBrands.length === 0 || !overpaymentId)
        return;

      setLoadingOtherOverpayments(true);
      try {
        // Only include partner_id in params if it's provided
        // Pass all brand IDs for cross-brand allocation
        const brandIds = selectedBrands.map((b) => b.id).join(",");
        const params = {
          brand: brandIds,
          overpayment_id: overpaymentId,
          has_remaining: true,
        };

        // Add partner_id to params only if it's provided
        if (partnerId) {
          params.partner_id = partnerId;
        }

        const response = await getOtherOverpaymentsForAllocation(params);

        // Handle different response formats
        let data;
        if (Array.isArray(response)) {
          data = response;
        } else if (response && Array.isArray(response.data)) {
          data = response.data;
        } else if (response && Array.isArray(response.results)) {
          data = response.results;
        } else {
          data = [];
        }

        // Process overpayments to add allocation fields
        const processedOverpayments = data.map((overpayment) => {
          // Check if the overpayment has existing allocation data from backend
          const existingAllocation = allocations.find(
            (alloc) => alloc.target_overpayment_id === overpayment.id
          );

          // Get allocation amount if it exists
          const allocationAmount = existingAllocation
            ? parseFloat(existingAllocation.amount || 0)
            : 0;

          // If this overpayment has an allocation, add it to selected overpayments
          if (
            allocationAmount > 0 &&
            !selectedOverpaymentIds.includes(overpayment.id)
          ) {
            setSelectedOverpaymentIds((prev) => [...prev, overpayment.id]);
          }

          return {
            ...overpayment,
            overpaymentAmount: allocationAmount > 0 ? allocationAmount : 0,
            overpaymentNotes: "",
            remainingAmount: parseFloat(overpayment.remaining_amount || 0),
          };
        });
        setOtherOverpayments(processedOverpayments);
      } catch (error) {
        console.error("Error fetching other overpayments:", error);
        setOtherOverpayments([]);

        // Display error message in notification
        let errorMessage = "Failed to load other overpayments";

        // Check if there's a specific error message from the API
        if (error.response?.data?.error) {
          errorMessage = error.response.data.error;
        }

        notification.error({
          message: "Overpayment Loading Error",
          description: errorMessage,
        });
      } finally {
        setLoadingOtherOverpayments(false);
      }
    },
    [
      selectedBrands,
      overpaymentId,
      allocations,
      selectedOverpaymentIds,
      setSelectedOverpaymentIds,
      setOtherOverpayments,
    ]
  );

  // Helper to process invoice data
  const processInvoiceData = useCallback(
    (data) => {
      const processedInvoices = data.map((invoice) => {
        const existingAllocation = allocations.find(
          (alloc) => alloc.invoice_id === invoice.id
        );

        const totalAmount = parseFloat(invoice.total_amount || 0);
        const paidAmount = parseFloat(invoice.paid_amount || 0);
        const remainingAmount = totalAmount - paidAmount;
        const allocationAmount = existingAllocation
          ? parseFloat(existingAllocation.amount || 0)
          : 0;

        return {
          ...invoice,
          invoice_date: invoice.issue_date,
          overpaymentAmount: allocationAmount > 0 ? allocationAmount : 0,
          overpaymentNotes: "",
          remainingAmount: remainingAmount,
          totalAmount,
          paidAmount,
          hasAllocation: allocationAmount > 0,
        };
      });

      // Deduplicate invoices by ID
      const uniqueInvoices = [];
      const seen = new Set();
      for (const inv of processedInvoices) {
        if (!seen.has(inv.id)) {
          seen.add(inv.id);
          uniqueInvoices.push(inv);
        }
      }
      return uniqueInvoices;
    },
    [allocations]
  );

  // Fetch invoices function - HYBRID LOADING: first page fast, rest in background
  const fetchInvoices = useCallback(
    async (partnerId, showPaidInvoices = false) => {
      if (!selectedBrands || selectedBrands.length === 0 || !overpaymentId)
        return;

      // Create a unique key for this request to prevent duplicates
      const fetchKey = `${overpaymentId}-${
        partnerId || "null"
      }-${showPaidInvoices}`;
      if (fetchingInvoicesRef.current && lastFetchKeyRef.current === fetchKey) {
        return; // Skip duplicate request
      }

      fetchingInvoicesRef.current = true;
      lastFetchKeyRef.current = fetchKey;

      // Abort any ongoing background loading
      backgroundAbortRef.current = true;
      setLoadingInvoices(true);

      try {
        const brandIds = selectedBrands.map((b) => b.id).join(",");
        const baseParams = {
          brand: brandIds,
          overpayment_id: overpaymentId,
          show_paid_invoices: showPaidInvoices,
        };
        if (partnerId) {
          baseParams.partner_id = partnerId;
        }

        // ========== PHASE 1: Quick first page load ==========
        const firstPageResult = await getFirstPageUnpaidInvoicesForOverpayment(
          baseParams
        );
        const firstPageData = firstPageResult.results || [];

        // Process and display first page immediately
        const processedFirstPage = processInvoiceData(firstPageData);
        setInvoices(processedFirstPage);
        setSelectedInvoiceIds(
          processedFirstPage
            .filter((inv) => inv.hasAllocation)
            .map((inv) => inv.id)
        );
        setLoadingInvoices(false);

        // ========== PHASE 2: Load remaining data in background ==========
        if (firstPageResult.hasMore) {
          backgroundAbortRef.current = false;
          backgroundLoadingRef.current = true;
          setIsLoadingMore(true);

          setTimeout(async () => {
            try {
              const fullResponse = await getUnpaidInvoicesForOverpayment(
                baseParams
              );

              if (backgroundAbortRef.current) {
                backgroundLoadingRef.current = false;
                setIsLoadingMore(false);
                return;
              }

              let fullData;
              if (Array.isArray(fullResponse)) {
                fullData = fullResponse;
              } else if (fullResponse && Array.isArray(fullResponse.data)) {
                fullData = fullResponse.data;
              } else if (fullResponse && Array.isArray(fullResponse.results)) {
                fullData = fullResponse.results;
              } else {
                fullData = [];
              }

              const processedFull = processInvoiceData(fullData);
              setInvoices(processedFull);
              setSelectedInvoiceIds(
                processedFull
                  .filter((inv) => inv.hasAllocation)
                  .map((inv) => inv.id)
              );
            } catch (bgError) {
              console.error("Background loading error:", bgError);
            } finally {
              backgroundLoadingRef.current = false;
              setIsLoadingMore(false);
            }
          }, 100);
        }
      } catch (error) {
        console.error("Error fetching invoices:", error);
        setInvoices([]);
        setLoadingInvoices(false);

        let errorMessage = "Failed to load partner invoices";
        if (error.response?.data?.error) {
          errorMessage = error.response.data.error;
        }

        notification.error({
          message: "Invoice Loading Error",
          description: errorMessage,
        });
      } finally {
        // Allow new requests after a short delay to prevent rapid re-fetches
        setTimeout(() => {
          fetchingInvoicesRef.current = false;
        }, 500);
      }
    },
    [selectedBrands, overpaymentId, processInvoiceData]
  );

  // NOTE: Initial invoice/overpayment fetching is handled inside fetchOverpayment
  // when the overpayment data loads and partner is determined.
  // This avoids duplicate requests that were previously caused by multiple useEffects.

  // Handle partner change
  const handlePartnerChange = (value) => {
    setSelectedPartner(value);
    // Clear data when partner changes
    setInvoices([]);
    setOtherOverpayments([]);
    // Fetch data for the new partner based on allocation type
    if (value) {
      if (allocationType === "invoice") {
        fetchInvoices(value, showPaidInvoices);
      } else {
        fetchOtherOverpayments(value);
      }
    }
  };

  // Handle allocation type change
  const handleAllocationTypeChange = (checked) => {
    const newType = checked ? "overpayment" : "invoice";
    setAllocationType(newType);

    // Reset selections when changing allocation type
    if (newType === "invoice") {
      setSelectedOverpaymentIds([]);
      // Fetch invoices if we have a partner
      if (selectedPartner) {
        fetchInvoices(selectedPartner, showPaidInvoices);
      } else {
        fetchInvoices(null, showPaidInvoices);
      }
    } else {
      setSelectedInvoiceIds([]);
      // Fetch other overpayments if we have a partner
      if (selectedPartner) {
        fetchOtherOverpayments(selectedPartner);
      } else {
        fetchOtherOverpayments(null);
      }
    }
  };

  // Handle overpayment allocation - wrapper for the imported function
  const handleAllocation = async (targetId, amount, allocationId = null) => {
    const currentItems =
      allocationType === "invoice" ? invoices : otherOverpayments;
    const setCurrentItems =
      allocationType === "invoice" ? setInvoices : setOtherOverpayments;
    const fetchCurrentItems =
      allocationType === "invoice" ? fetchInvoices : fetchOtherOverpayments;

    const success = await allocateOverpaymentHandler(
      overpaymentId,
      targetId,
      amount,
      allocations,
      currentItems,
      allocationId,
      selectedPartner,
      setLoadingAllocation,
      setCurrentItems,
      setAllocations,
      fetchOverpayment,
      fetchCurrentItems,
      allocationType
    );

    // Notify parent component with overpaymentId to update just that row
    if (success && onSuccess) {
      onSuccess({ overpaymentId, type: "overpayment" });
    }

    return success;
  };

  const handleOverpaymentChange = (itemId, e) => {
    // Get the raw input value
    const inputValue = e.target.value;

    if (allocationType === "invoice") {
      // Update the overpayment amount for the specific invoice
      const updatedInvoices = invoices.map((invoice) => {
        if (invoice.id === itemId) {
          // Store the raw input value to prevent cursor jumping
          // Only convert to number when actually submitting
          return {
            ...invoice,
            overpaymentAmount: inputValue,
          };
        }
        return invoice;
      });

      setInvoices(updatedInvoices);

      // Don't auto-select rows when typing - let users manually check rows
      // This prevents the cursor jumping issue when typing in input fields
    } else {
      // Update the overpayment amount for the specific overpayment
      const updatedOverpayments = otherOverpayments.map((overpayment) => {
        if (overpayment.id === itemId) {
          return {
            ...overpayment,
            overpaymentAmount: inputValue,
          };
        }
        return overpayment;
      });

      setOtherOverpayments(updatedOverpayments);

      // Don't auto-select rows when typing - let users manually check rows
      // This prevents the cursor jumping issue when typing in input fields
    }
  };

  // Handle allocate all checked items
  const handleAllocateAllChecked = async () => {
    const selectedIds =
      allocationType === "invoice"
        ? selectedInvoiceIds
        : selectedOverpaymentIds;
    const items = allocationType === "invoice" ? invoices : otherOverpayments;

    if (selectedIds.length === 0) {
      message.warning("Please select at least one item to allocate.");
      return;
    }

    let remainingToAllocate = Math.abs(
      parseFloat(overpayment?.remaining_amount || 0)
    );
    let successCount = 0;
    let failCount = 0;

    for (const itemId of selectedIds) {
      if (remainingToAllocate <= 0) {
        break;
      }

      const item = items.find((i) => i.id === itemId);
      if (!item) continue;

      // Calculate allocation amount
      let allocationAmount;
      if (allocationType === "invoice") {
        const invoiceRemaining = parseFloat(item.remainingAmount || 0);
        allocationAmount = Math.min(remainingToAllocate, invoiceRemaining);
      } else {
        // For overpayment-to-overpayment, use the target's remaining amount
        const targetRemaining = Math.abs(
          parseFloat(item.remaining_amount || 0)
        );
        allocationAmount = Math.min(remainingToAllocate, targetRemaining);
      }

      if (allocationAmount <= 0) continue;

      try {
        const success = await handleAllocation(itemId, allocationAmount);
        if (success) {
          successCount++;
          remainingToAllocate -= allocationAmount;
        } else {
          failCount++;
        }
      } catch (error) {
        failCount++;
      }
    }

    // Clear selections after processing
    if (allocationType === "invoice") {
      setSelectedInvoiceIds([]);
    } else {
      setSelectedOverpaymentIds([]);
    }

    if (successCount > 0) {
      message.success(`Successfully allocated ${successCount} item(s).`);
    }
    if (failCount > 0) {
      message.error(`Failed to allocate ${failCount} item(s).`);
    }
  };

  // Update allocation status from overpayment data
  const updateAllocationStatus = (overpayment) => {
    if (!overpayment) {
      setAllocationStatus(null);
      return;
    }

    try {
      const overpaymentAmount = parseFloat(overpayment.amount || 0);
      const remainingAmount = parseFloat(overpayment.remaining_amount || 0);

      // Calculate allocated amount correctly for both positive (debit) and negative (credit) overpayments
      let totalAllocated;
      if (overpaymentAmount >= 0) {
        // Debit overpayment: simple subtraction
        totalAllocated = overpaymentAmount - remainingAmount;
      } else {
        // Credit overpayment: use absolute values since both are negative
        // When fully allocated, remaining goes from -$25,000 to $0
        // Allocated = |-$25,000| - |$0| = $25,000
        totalAllocated =
          Math.abs(overpaymentAmount) - Math.abs(remainingAmount);
      }

      // Always use absolute value for percentage calculation
      const absAmount = Math.abs(overpaymentAmount);
      const percentAllocated =
        absAmount > 0 ? (totalAllocated / absAmount) * 100 : 0;

      // Determine status based on remaining amount (more accurate than percentage)
      let status = null;
      // Only mark as fully allocated if remaining is essentially 0
      if (Math.abs(remainingAmount) <= 0.001) {
        status = {
          type: "fully_allocated",
          label: "Fully Allocated",
          color: "success",
        };
      } else if (totalAllocated > 0) {
        status = {
          type: "partially_allocated",
          label: "Partially Allocated",
          color: "warning",
        };
      } else {
        status = {
          type: "unallocated",
          label: "Unallocated",
          color: "default",
        };
      }

      setAllocationStatus({
        ...status,
        totalAllocated,
        percentAllocated,
      });
    } catch (error) {
      console.error("Error updating allocation status:", error);
      setAllocationStatus({
        type: "error",
        label: "Error",
        color: "error",
        totalAllocated: 0,
        percentAllocated: 0,
      });
    }
  };

  // Handle show paid invoices toggle - trigger refetch when changed
  const handleShowPaidInvoicesChange = (checked) => {
    setShowPaidInvoices(checked);
    // Immediately refetch invoices with the new setting if we're in invoice mode
    if (allocationType === "invoice") {
      fetchInvoices(selectedPartner, checked);
    }
  };

  // Sort invoices by oldest to newest (issue_date ascending) for allocation
  const sortedInvoices = useMemo(() => {
    return [...invoices].sort((a, b) => {
      const dateA = a.invoice_date ? new Date(a.invoice_date) : new Date(0);
      const dateB = b.invoice_date ? new Date(b.invoice_date) : new Date(0);
      return dateA - dateB; // Oldest first
    });
  }, [invoices]);

  // Sort overpayments by oldest to newest
  const sortedOverpayments = useMemo(() => {
    return [...otherOverpayments].sort((a, b) => {
      const dateA = a.created_date ? new Date(a.created_date) : new Date(0);
      const dateB = b.created_date ? new Date(b.created_date) : new Date(0);
      return dateA - dateB; // Oldest first
    });
  }, [otherOverpayments]);

  // Handle back button
  const handleBack = () => {
    if (onCancel) {
      onCancel();
    } else {
      navigate("/accounting/overpayments");
    }
  };

  if (!overpaymentId || !selectedBrands || selectedBrands.length === 0) {
    return (
      <Card title="Overpayment Allocation">
        <Text type="secondary">
          No overpayment selected or brand not selected
        </Text>
        <div style={{ marginTop: 16 }}>
          <Button
            type="primary"
            icon={<ArrowLeftOutlined />}
            onClick={handleBack}
          >
            Back to Overpayments
          </Button>
        </div>
      </Card>
    );
  }

  return (
    <Card size="small" className="overpayment-allocation-card">
      {loadingOverpayment ? (
        <div style={{ textAlign: "center", padding: "50px" }}>
          <Spin size="large" />
        </div>
      ) : (
        <>
          <Title level={5}>Overpayment Details</Title>
          {overpayment && (
            <Table
              dataSource={[overpayment]}
              columns={getOverpaymentDetailColumns(allocationStatus)}
              pagination={false}
              rowKey="overpayment_id"
              size="small"
            />
          )}

          <Divider />

          <Row gutter={16} justify="space-between" align="middle">
            <Col span={8}>
              <div style={{ marginBottom: 16 }}>
                <Text strong>Select Partner:</Text>
                <Select
                  placeholder="Select a partner"
                  style={{ width: "100%", marginTop: 8 }}
                  onChange={handlePartnerChange}
                  value={selectedPartner}
                  showSearch
                  optionFilterProp="children"
                >
                  {Array.isArray(partners) && partners.length > 0 ? (
                    partners.map((partner) => (
                      <Option key={partner.id} value={partner.id}>
                        {partner.name} ({partner.partner_type})
                      </Option>
                    ))
                  ) : (
                    <Option value="" disabled>
                      No partners available
                    </Option>
                  )}
                </Select>
              </div>
            </Col>
            <Col span={8}>
              <div style={{ marginBottom: 16 }}>
                <Text strong>Allocation Type:</Text>
                <div
                  style={{
                    marginTop: 8,
                    display: "flex",
                    alignItems: "center",
                  }}
                >
                  <FileTextOutlined style={{ marginRight: 8 }} />
                  <Switch
                    checked={allocationType === "overpayment"}
                    onChange={handleAllocationTypeChange}
                    style={{ margin: "0 8px" }}
                  />
                  <DollarOutlined />
                  <Text style={{ marginLeft: 8 }}>
                    {allocationType === "invoice"
                      ? "Allocate to Invoices"
                      : "Allocate to Other Overpayments"}
                  </Text>
                </div>
              </div>
            </Col>
            <Col>
              <div
                style={{
                  marginBottom: 16,
                  display: "flex",
                  alignItems: "center",
                }}
              >
                <Space>
                  <span>Show Paid</span>
                  <Switch
                    checked={showPaidInvoices}
                    onChange={handleShowPaidInvoicesChange}
                    disabled={allocationType !== "invoice"}
                  />
                </Space>
              </div>
            </Col>
          </Row>

          <div
            style={{
              display: "flex",
              justifyContent: "space-between",
              alignItems: "center",
              marginTop: 16,
              marginBottom: 16,
            }}
          >
            <Title level={5} style={{ margin: 0 }}>
              {allocationType === "invoice"
                ? "Partner Invoices"
                : "Other Overpayments"}
            </Title>
            <Button
              type="primary"
              onClick={handleAllocateAllChecked}
              disabled={
                loadingAllocation ||
                (allocationType === "invoice"
                  ? selectedInvoiceIds.length === 0
                  : selectedOverpaymentIds.length === 0)
              }
              loading={loadingAllocation}
            >
              Allocate All Checked
            </Button>
          </div>
          {allocationType === "invoice" ? (
            <div className="fixed-table-container">
              <Table
                dataSource={sortedInvoices}
                columns={getInvoiceColumns({
                  handleOverpaymentChange,
                  handleAllocateOverpayment: handleAllocation,
                  allocations,
                  loadingAllocation,
                  remainingAmount: overpayment
                    ? Math.abs(parseFloat(overpayment.remaining_amount || 0))
                    : 0,
                  parseNumberFn: parseNumberWithCommas,
                  onViewPartner,
                })}
                pagination={{
                  ...pagination,
                  total: sortedInvoices.length,
                  showSizeChanger: true,
                  showQuickJumper: true,
                  pageSizeOptions: ["50", "100", "200"],
                  showTotal: (total) => `Total ${total} invoices`,
                  onChange: (page, pageSize) => {
                    setPagination({ current: page, pageSize });
                  },
                  onShowSizeChange: (current, size) => {
                    setPagination({ current: 1, pageSize: size });
                  },
                }}
                rowKey="id"
                size="small"
                loading={loadingInvoices}
                rowClassName={(record) => {
                  const hasAllocation =
                    allocations && Array.isArray(allocations)
                      ? allocations.some(
                          (alloc) => alloc.invoice_id === record.id
                        )
                      : false;
                  return hasAllocation ? "table-success-outline-invoice" : "";
                }}
                scroll={{ y: 400 }}
                tableLayout="auto"
                sticky={{ offsetHeader: 0 }}
                rowSelection={{
                  selectedRowKeys: selectedInvoiceIds,
                  onChange: (selectedRowKeys) =>
                    setSelectedInvoiceIds(selectedRowKeys),
                  getCheckboxProps: (record) => ({
                    disabled:
                      parseFloat(record.remainingAmount) <= 0 ||
                      record.status === "paid" ||
                      record.status === "cancelled",
                  }),
                }}
              />
            </div>
          ) : (
            <div className="fixed-table-container">
              <Table
                dataSource={sortedOverpayments}
                columns={getOverpaymentColumns({
                  handleOverpaymentChange,
                  handleAllocateOverpayment: handleAllocation,
                  allocations,
                  loadingAllocation,
                  remainingAmount: overpayment
                    ? Math.abs(parseFloat(overpayment.remaining_amount || 0))
                    : 0,
                  parseNumberFn: parseNumberWithCommas,
                  onViewPartner,
                })}
                pagination={{
                  ...pagination,
                  total: sortedOverpayments.length,
                  showSizeChanger: true,
                  showQuickJumper: true,
                  pageSizeOptions: ["50", "100", "200"],
                  showTotal: (total) => `Total ${total} overpayments`,
                  onChange: (page, pageSize) => {
                    setPagination({ current: page, pageSize });
                  },
                  onShowSizeChange: (current, size) => {
                    setPagination({ current: 1, pageSize: size });
                  },
                }}
                rowKey="id"
                size="small"
                loading={loadingOtherOverpayments}
                rowClassName={(record) => {
                  const hasAllocation =
                    allocations && Array.isArray(allocations)
                      ? allocations.some(
                          (alloc) => alloc.target_overpayment_id === record.id
                        )
                      : false;
                  return hasAllocation ? "table-success-outline-invoice" : "";
                }}
                scroll={{ y: 400 }}
                tableLayout="auto"
                sticky={{ offsetHeader: 0 }}
                rowSelection={{
                  selectedRowKeys: selectedOverpaymentIds,
                  onChange: (selectedRowKeys) =>
                    setSelectedOverpaymentIds(selectedRowKeys),
                  getCheckboxProps: (record) => ({
                    disabled:
                      Math.abs(parseFloat(record.remaining_amount || 0)) <= 0,
                  }),
                }}
              />
            </div>
          )}
        </>
      )}
    </Card>
  );
};

export default OverPaymentAllocation;
